import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  FiCheck, FiChevronDown, FiShield, 
  FiActivity, FiZap, FiMail, FiServer, FiMonitor, FiLayers, 
  FiTool, FiClock, FiRefreshCw, FiSearch, FiAlertCircle 
} from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { pageFaqs } from '../data/faqs';

const BusinessEmail = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = "Business Email Services | ManagedSite.net";
    
    const description = "ManagedSite.net helps with Microsoft 365, Google Workspace, email migrations, SPF DKIM DMARC, deliverability and website transactional email reliability.";
    const title = "Business Email Services | ManagedSite.net";
    
    const updateMeta = (selector, attr, value) => {
      const el = document.querySelector(selector);
      if (el) el.setAttribute(attr, value);
    };
    
    updateMeta('meta[name="description"]', 'content', description);
    updateMeta('meta[property="og:title"]', 'content', title);
    updateMeta('meta[property="og:description"]', 'content', description);
    updateMeta('meta[property="twitter:title"]', 'content', title);
    updateMeta('meta[property="twitter:description"]', 'content', description);
  }, []);

  const [openFaq, setOpenFaq] = useState(null);

  const whyMatters = [
    {
      title: "Missed Client Messages",
      text: "Important business email can land in spam or fail to send without clear warning.",
      icon: FiAlertCircle
    },
    {
      title: "Domain and DNS Complexity",
      text: "MX records, SPF, DKIM and DMARC must stay aligned with the mailbox provider and website.",
      icon: FiLayers
    },
    {
      title: "Website Email Failures",
      text: "Contact forms, WooCommerce notices and password resets often fail even when the website looks fine.",
      icon: FiMonitor
    },
    {
      title: "Migration Risk",
      text: "Moving mailboxes between providers can interrupt operations if DNS and cutover are handled poorly.",
      icon: FiRefreshCw
    }
  ];

  const coreServices = [
    {
      title: "Microsoft 365 Setup and Administration",
      description: "Setup and ongoing administration support for Microsoft 365 business email environments.",
      examples: ["Tenant and domain setup", "Mailbox creation", "Shared mailboxes", "Aliases", "User onboarding and offboarding"],
      icon: FiMail
    },
    {
      title: "Google Workspace Setup and Administration",
      description: "Setup and administration support for Google Workspace business email.",
      examples: ["Domain verification", "Mailbox provisioning", "Groups and aliases", "User onboarding and offboarding", "Admin console support"],
      icon: FiMail
    },
    {
      title: "Email Migrations",
      description: "Planned mailbox migrations between providers with DNS and cutover coordination.",
      examples: ["Microsoft 365 migrations", "Google Workspace migrations", "Provider-to-provider moves", "DNS cutover planning", "Post-migration validation"],
      icon: FiRefreshCw
    },
    {
      title: "Authentication and Deliverability",
      description: "Review and configuration support for email authentication and delivery health.",
      examples: ["SPF review", "DKIM review", "DMARC monitoring", "Delivery troubleshooting", "Unauthorized sender monitoring"],
      icon: FiShield
    },
    {
      title: "Website Transactional Email",
      description: "Improve delivery of website-generated email such as forms, password resets and order notices.",
      examples: ["Contact form delivery", "Password reset email", "WooCommerce notifications", "Postmark setup when needed", "WordPress email log review"],
      icon: FiZap
    },
    {
      title: "Email Troubleshooting",
      description: "Diagnose and resolve email problems affecting mailboxes, authentication or website delivery.",
      examples: ["Mail flow issues", "DNS and MX problems", "Spam and delivery failures", "Form email failures", "Provider configuration conflicts"],
      icon: FiTool
    }
  ];

  const faqs = pageFaqs['/services/business-email'];

  return (
    <div className="w-full">
      {/* Hero Section */}
      <section className="relative bg-[#FAF8F3] overflow-hidden pt-24 pb-24 lg:pt-40 lg:pb-36">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="lg:grid lg:grid-cols-12 lg:gap-20 items-center">
            <div className="lg:col-span-7 text-center lg:text-left mb-20 lg:mb-0">
              <div className="text-sm font-bold tracking-[0.25em] text-[#D7B77A] uppercase mb-8">Business Email</div>
              <h1 className="text-5xl sm:text-6xl lg:text-7xl font-extrabold text-[#17211E] tracking-tight leading-[1.1] mb-10">
                Keep Business Email Reliable, Secure and Under Control
              </h1>
              <p className="text-xl sm:text-2xl text-[#17211E]/80 leading-relaxed mb-12 max-w-2xl mx-auto lg:mx-0 font-medium">
                Microsoft 365, Google Workspace, migrations, authentication, and website email delivery — without adding an internal email specialist.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
                <Link to="/digital-review" className="home-button">
                  Request Email Support
                </Link>
                <Link to="/agency-plans" className="home-button-secondary">
                  View Agency Plans
                </Link>
              </div>
            </div>
            <div className="lg:col-span-5 relative">
              <div className="relative rounded-[2rem] bg-[#EAF1EC] border border-[#3B7C6D]/20 shadow-2xl p-10 sm:p-12">
                <div className="absolute top-0 left-0 w-full h-2 bg-[#174C3C]"></div>
                <div className="space-y-6">
                  {[
                    { icon: FiMail, label: "Microsoft 365", status: "Managed" },
                    { icon: FiServer, label: "Google Workspace", status: "Supported" },
                    { icon: FiShield, label: "SPF DKIM DMARC", status: "Reviewed" },
                    { icon: FiRefreshCw, label: "Migrations", status: "Available" },
                    { icon: FiZap, label: "Transactional Email", status: "Monitored" },
                    { icon: FiActivity, label: "Deliverability", status: "Protected" }
                  ].map((item, i) => (
                    <div key={i} className="bg-white p-5 rounded-2xl flex items-center justify-between border border-gray-50">
                      <div className="flex items-center gap-4">
                        <div className="bg-[#EAF1EC] p-3 rounded-xl text-[#174C3C]">
                          <SafeIcon icon={item.icon} className="w-6 h-6" />
                        </div>
                        <span className="font-extrabold text-[#17211E]">{item.label}</span>
                      </div>
                      <span className="text-sm font-bold text-[#3B7C6D] bg-[#EAF1EC] px-3 py-1 rounded-full">{item.status}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Email Support Matters */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-20">
            <h2 className="text-4xl md:text-5xl font-bold text-[#17211E] mb-8 leading-tight">Email Problems Affect More Than Inboxes</h2>
            <p className="text-xl text-[#17211E]/70 leading-relaxed font-medium">
              Business email connects domains, DNS, authentication, devices, websites and client communication. When delivery fails, forms stop working, password resets break, and trust drops quickly.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {whyMatters.map((item, i) => (
              <div key={i} className="bg-[#FAF8F3] p-10 rounded-3xl border border-[#EAF1EC] hover:shadow-md transition-all">
                <div className="text-[#174C3C] mb-8">
                  <SafeIcon icon={item.icon} className="w-10 h-10" />
                </div>
                <h3 className="text-2xl font-bold text-[#17211E] mb-4">{item.title}</h3>
                <p className="text-lg text-[#17211E]/75 leading-relaxed font-medium">{item.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Core Email Services */}
      <section className="py-24 bg-[#FAF8F3]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-20">
            <h2 className="text-4xl md:text-5xl font-bold text-[#17211E] mb-8 leading-tight">What Business Email Support Includes</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10">
            {coreServices.map((service, i) => (
              <div key={i} className="bg-white p-10 md:p-12 rounded-[2rem] border border-[#EAF1EC] shadow-sm flex flex-col h-full">
                <h3 className="text-2xl font-extrabold text-[#17211E] mb-6">{service.title}</h3>
                <p className="text-xl text-[#17211E]/75 mb-10 leading-relaxed font-medium">{service.description}</p>
                <div className="flex-grow">
                  <div className="text-sm font-bold text-[#3B7C6D] uppercase tracking-wider mb-6">Examples:</div>
                  <ul className="space-y-4">
                    {service.examples.map((item, j) => (
                      <li key={j} className="flex items-start text-[#17211E]/80 text-lg font-medium">
                        <SafeIcon icon={FiCheck} className="w-5 h-5 text-[#174C3C] mr-4 mt-1 flex-shrink-0" />
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Separate Services Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-20">
            <h2 className="text-4xl md:text-5xl font-bold text-[#17211E] mb-8 leading-tight">Mailbox Support and Website Email Are Different</h2>
            <p className="text-xl text-[#17211E]/75 leading-relaxed font-medium">
              General mailbox administration is not automatically included in WordPress care plans. Website transactional email can be handled as a separate Email Reliability service.
            </p>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 mb-16">
            <div className="bg-[#FAF8F3] p-12 rounded-[2.5rem] border border-[#EAF1EC] shadow-sm">
              <h3 className="text-2xl font-extrabold text-[#17211E] mb-8 border-b border-[#EAF1EC] pb-6 uppercase tracking-wider">Business Email Administration</h3>
              <ul className="space-y-6 mb-10">
                {["Microsoft 365 support", "Google Workspace support", "Mailbox creation and changes", "Shared mailboxes and aliases", "User onboarding and offboarding", "Email migrations"].map((item, i) => (
                  <li key={i} className="flex items-center text-xl font-bold text-[#17211E]">
                    <div className="w-2 h-2 bg-[#174C3C] rounded-full mr-4 flex-shrink-0"></div>
                    {item}
                  </li>
                ))}
              </ul>
              <p className="text-lg font-black text-[#174C3C] italic">Billed as project work or hourly agency support</p>
            </div>
            <div className="bg-[#EAF1EC] p-12 rounded-[2.5rem] border border-[#3B7C6D]/10 shadow-sm">
              <h3 className="text-2xl font-extrabold text-[#17211E] mb-8 border-b border-[#3B7C6D]/10 pb-6 uppercase tracking-wider">Email Reliability</h3>
              <ul className="space-y-6 mb-10">
                {["DMARC monitoring", "SPF and DKIM review", "Postmark transactional email when needed", "Contact-form delivery monitoring", "Authentication and delivery alerts", "Unauthorized sender monitoring"].map((item, i) => (
                  <li key={i} className="flex items-center text-xl font-bold text-[#17211E]">
                    <SafeIcon icon={FiCheck} className="w-6 h-6 text-[#174C3C] mr-4 flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
              <p className="text-lg font-black text-[#174C3C] italic">Managed as an ongoing subscription add-on</p>
            </div>
          </div>
          <div className="max-w-4xl mx-auto bg-white border-2 border-dashed border-[#EAF1EC] p-8 rounded-2xl text-center">
            <p className="text-lg text-[#17211E]/60 font-bold italic">
              Important note: Email Reliability does not include Microsoft 365 mailbox support, Google Workspace mailbox support, mailbox migrations, employee device support, marketing email or high-volume sending.
            </p>
          </div>
        </div>
      </section>

      {/* Email Reliability Pricing */}
      <section className="py-24 bg-[#EAF1EC]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-[3rem] p-12 md:p-20 border border-[#3B7C6D]/10 shadow-xl">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-20 items-center">
              <div>
                <h2 className="text-4xl font-bold text-[#17211E] mb-6">Email Reliability Pricing</h2>
                <p className="text-xl text-[#17211E]/75 mb-10 font-medium leading-relaxed">
                  Email Reliability helps agencies protect website-generated email delivery and domain authentication health.
                </p>
                <div className="space-y-4 mb-10">
                  {["Improves delivery of contact forms", "Improves password reset delivery", "Improves WooCommerce notification delivery", "Monitors authentication problems", "Monitors unauthorized senders and spoofing"].map((item, i) => (
                    <div key={i} className="flex items-center gap-3 text-lg font-bold text-[#17211E]/80">
                      <SafeIcon icon={FiCheck} className="text-[#174C3C]" />
                      {item}
                    </div>
                  ))}
                </div>
                <div className="flex flex-wrap gap-6">
                  <div className="bg-[#FAF8F3] px-6 py-3 rounded-xl border border-[#EAF1EC] font-bold text-[#17211E]">
                    Postmark setup: $49 one time
                  </div>
                  <div className="bg-[#FAF8F3] px-6 py-3 rounded-xl border border-[#EAF1EC] font-bold text-[#17211E]">
                    Postmark service: $10/month
                  </div>
                </div>
              </div>
              <div className="bg-[#FAF8F3] p-12 rounded-[2.5rem] border border-[#EAF1EC]">
                <div className="text-center mb-10">
                  <div className="text-6xl font-black text-[#174C3C] mb-2">$15<span className="text-2xl text-[#17211E]/50">/mo</span></div>
                  <div className="text-xl font-bold text-[#17211E]/70">per domain</div>
                </div>
                <div className="space-y-6 border-t border-[#EAF1EC] pt-10">
                  <div className="flex justify-between items-center text-xl font-bold text-[#17211E]">
                    <span>Initial Configuration</span>
                    <span className="text-[#174C3C]">$49 one time</span>
                  </div>
                  <div className="flex justify-between items-center text-xl font-bold text-[#17211E] pt-6 border-t border-[#EAF1EC]">
                    <span>Extra Remediation</span>
                    <span className="text-[#174C3C]">Partner rate</span>
                  </div>
                </div>
                <Link to="/digital-review" className="home-button w-full mt-12">
                  Get Started with Email Reliability
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Agency White-Label Section */}
      <section className="py-32 bg-[#174C3C] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-20 items-center">
            <div>
              <div className="text-sm font-bold tracking-[0.25em] text-[#D7B77A] uppercase mb-8">For Agencies</div>
              <h2 className="text-4xl md:text-5xl lg:text-6xl font-extrabold mb-10 leading-tight">Email Support Delivered Behind Your Brand</h2>
              <p className="text-2xl text-[#FAF8F3]/80 mb-12 font-medium leading-relaxed">
                ManagedSite.net can handle email setup, migrations, authentication and delivery issues while your agency remains the client-facing provider.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-12 gap-y-6 mb-16">
                {["White-label delivery", "Agency support rate available", "No direct solicitation of clients", "Coordination with PMs", "Clear handoff documentation"].map((item, i) => (
                  <div key={i} className="flex items-start text-xl font-bold">
                    <SafeIcon icon={FiCheck} className="w-6 h-6 text-[#D7B77A] mr-4 flex-shrink-0 mt-1" />
                    {item}
                  </div>
                ))}
              </div>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/contact" className="home-button-on-dark">
                  Request Email Support
                </Link>
                <Link to="/agency-plans" className="home-button-on-dark-secondary">
                  View Agency Plans
                </Link>
              </div>
              <div className="mt-12 pt-12 border-t border-white/10">
                <div className="text-2xl font-black text-[#D7B77A] mb-2">Agency technical support uses a partner rate</div>
                <p className="text-white/60 font-bold italic">Prepaid blocks available on the Technical Support page and Agency Plans page.</p>
              </div>
            </div>
            <div className="hidden lg:block relative select-none opacity-50">
              <div className="bg-white/5 rounded-[3rem] p-16 border border-white/10 backdrop-blur-sm text-center">
                <div className="text-[#D7B77A] text-6xl font-black mb-10">EMAIL</div>
                <div className="h-1.5 w-24 bg-[#D7B77A] mx-auto mb-10"></div>
                <div className="space-y-6">
                  <div className="h-4 bg-white/20 rounded-full w-full"></div>
                  <div className="h-4 bg-white/20 rounded-full w-5/6 mx-auto"></div>
                  <div className="h-4 bg-white/20 rounded-full w-4/6 mx-auto"></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How Email Projects Work */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl md:text-5xl font-bold text-[#17211E] mb-20 text-center">How Business Email Support Works</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
            {[
              { title: "Review", text: "We review the current provider, DNS, authentication, mailbox needs and website email requirements." },
              { title: "Plan", text: "We define the setup, migration or remediation plan and confirm access requirements." },
              { title: "Implement", text: "We configure mailboxes, DNS, authentication or transactional email systems as needed." },
              { title: "Validate", text: "We test delivery, confirm key workflows and document the final setup." }
            ].map((step, i) => (
              <div key={i} className="relative">
                <div className="text-6xl font-black text-[#EAF1EC] mb-8">{i + 1}</div>
                <h3 className="text-2xl font-bold text-[#17211E] mb-5">{step.title}</h3>
                <p className="text-lg text-[#17211E]/75 leading-relaxed font-medium">{step.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Common Requests */}
      <section className="py-24 bg-[#FAF8F3]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl md:text-5xl font-bold text-[#17211E] mb-16 text-center">Common Email Requests We Handle</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-16">
            {[
              "Microsoft 365 setup", "Google Workspace setup", "Mailbox migrations", "Shared mailbox creation", 
              "Alias configuration", "SPF, DKIM and DMARC issues", "Contact forms not delivering", 
              "WooCommerce email failures", "Password reset email failures", 
              "Domain connected to the wrong mail provider", "Postmark setup", 
              "Deliverability troubleshooting after a migration"
            ].map((item, i) => (
              <div key={i} className="bg-white p-6 rounded-2xl border border-[#EAF1EC] font-bold text-[#17211E] text-lg flex items-center gap-4 shadow-sm">
                <div className="w-2 h-2 rounded-full bg-[#174C3C]"></div>
                {item}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Business Routing Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-[#FAF8F3] rounded-[2.5rem] p-12 md:p-20 shadow-xl border border-[#EAF1EC] text-center max-w-5xl mx-auto">
            <div className="text-sm font-bold tracking-[0.25em] text-[#D7B77A] uppercase mb-8">For Businesses</div>
            <h2 className="text-4xl md:text-5xl font-extrabold text-[#17211E] mb-8 leading-tight">Need Email Support for Your Own Business?</h2>
            <p className="text-xl text-[#17211E]/75 mb-12 font-medium leading-relaxed max-w-3xl mx-auto">
              Tell us what is happening with email, and we will help work out the next step.
            </p>
            <Link to="/digital-review" className="home-button">
              Tell Us What You Need Help With
            </Link>
          </div>
        </div>
      </section>

      {/* Related Services */}
      <section className="py-24 bg-[#FAF8F3]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold text-[#17211E] mb-16">Related Services</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-6 mb-16">
            {[
              { label: "Technical Support", link: "/services/technical-support" },
              { label: "Website Management", link: "/services/website-management" },
              { label: "Agency Plans", link: "/agency-plans" },
              { label: "Managed Hosting", link: "/services/managed-hosting" },
              { label: "Technology Consulting", link: "/services/technology-consulting" }
            ].map((item, i) => (
              <Link key={i} to={item.link} className="bg-white p-8 rounded-2xl border border-[#EAF1EC] font-bold text-[#17211E] text-center hover:shadow-md transition-all">
                {item.label}
              </Link>
            ))}
          </div>
          <Link to="/services" className="home-button-secondary">
            Explore All Services
          </Link>
        </div>
      </section>

      {/* FAQ Accordion */}
      <section className="py-24 bg-white border-t border-[#EAF1EC]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-[#17211E] mb-16 text-center">Frequently Asked Questions</h2>
          <div className="space-y-4">
            {faqs.map((faq, i) => (
              <div key={i} className="bg-[#FAF8F3] border border-[#EAF1EC] rounded-2xl overflow-hidden shadow-sm">
                <button 
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  aria-expanded={openFaq === i}
                  aria-controls={`faq-answer-${i}`}
                  className="w-full flex justify-between items-center p-8 text-left hover:bg-white transition-colors"
                >
                  <span className="text-xl font-bold text-[#17211E]">{faq.q}</span>
                  <SafeIcon icon={FiChevronDown} className={`w-6 h-6 text-[#3B7C6D] transition-transform ${openFaq === i ? 'rotate-180' : ''}`} />
                </button>
                <div 
                  id={`faq-answer-${i}`} 
                  className={`px-8 pb-8 text-xl text-[#17211E]/75 font-medium leading-relaxed ${openFaq === i ? 'block' : 'hidden'}`}
                >
                  {faq.a}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-32 bg-[#174C3C] text-center relative overflow-hidden">
        <div className="absolute top-0 left-0 w-64 h-64 bg-white/5 rounded-full -translate-x-1/2 -translate-y-1/2"></div>
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-white/5 rounded-full translate-x-1/3 translate-y-1/3"></div>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-10 tracking-tight leading-tight">
            Make Business Email One Less Thing You Have to Chase
          </h2>
          <p className="text-2xl text-[#FAF8F3]/80 mb-14 max-w-3xl mx-auto leading-relaxed">
            Get dependable help with Microsoft 365, Google Workspace, migrations, authentication and website email delivery.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <Link to="/digital-review" className="home-button-on-dark">
              Request Email Support
            </Link>
            <Link to="/agency-plans" className="home-button-on-dark-secondary">
              View Agency Plans
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default BusinessEmail;